import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check, ChevronDown, Mail, Wallet, Eye, Globe, Sparkles, ShieldCheck, Zap, Trophy, MessageCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import VisitorIntentPrompt from '@/components/homepage/VisitorIntentPrompt';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { getStoredUtms } from '@/lib/utm';
import { cn } from '@/lib/utils';

const HOME_DESCRIPTION =
  "Sport Fund is the free, athlete-first platform that lets family, friends, club and sponsors back your career — equipment, training, travel, competition — straight to your bank account.";

export default function Home() {
  usePageMeta(undefined, HOME_DESCRIPTION);
  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <HomeHeader transparent />
      <Hero />
      <TrustStrip />
      <MoneyMath />
      <HowItWorks />
      <FeaturesBento />
      <DemoShowcase />
      <FaqTeaser />
      <FinalCTA />
      <Footer />
      <VisitorIntentPrompt />
    </div>
  );
}

/* ─────────────────────────────────────────────── Hero ── */

function Hero() {
  const [fromChatGPT, setFromChatGPT] = useState(false);

  useEffect(() => {
    setFromChatGPT(getStoredUtms().utm_source === 'chatgpt.com');
  }, []);

  return (
    <section className="relative overflow-hidden bg-[#070b18] text-white">
      {/* Background gradient + dot grid + blobs */}
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_-10%,rgba(30,64,175,0.45),transparent_60%),radial-gradient(80%_60%_at_100%_100%,rgba(250,204,21,0.18),transparent_55%)]"
      />
      <div aria-hidden className="absolute inset-0 opacity-[0.07] [background-image:radial-gradient(rgba(255,255,255,0.7)_1px,transparent_1px)] [background-size:24px_24px]" />
      <div className="absolute -top-32 -left-32 h-[480px] w-[480px] rounded-full bg-blue-600/20 blur-3xl hero-blob" style={{ animation: 'blobFloat1 18s ease-in-out infinite' }} aria-hidden />
      <div className="absolute -bottom-32 right-0 h-[520px] w-[520px] rounded-full bg-yellow-400/10 blur-3xl hero-blob" style={{ animation: 'blobFloat2 22s ease-in-out infinite' }} aria-hidden />

      <div className="relative mx-auto max-w-7xl px-6 pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Copy */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 text-xs font-medium tracking-wider uppercase text-yellow-300/90">
              <span className="h-1.5 w-1.5 rounded-full bg-yellow-400 animate-pulse" />
              For athletes chasing something
            </div>

            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-extrabold tracking-tight leading-[0.95] text-balance">
              Train hard.
              <br />
              Don&apos;t carry the
              <br />
              <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent">
                season alone.
              </span>
            </h1>

            <p className="mt-7 max-w-2xl text-lg md:text-xl text-slate-300 leading-relaxed">
              Sport Fund is the free, athlete-first platform that lets family, friends, club and sponsors back your career — equipment, travel, coaching, competition. Set up a fund page in 2 minutes. Get paid directly. <span className="text-white font-medium">Zero platform skim.</span>
            </p>

            {fromChatGPT && (
              <div className="mt-5 flex max-w-2xl items-start gap-2.5 rounded-xl border border-yellow-400/25 bg-yellow-400/10 px-4 py-3 text-sm text-yellow-100">
                <MessageCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
                <span>
                  Referred here by ChatGPT? Sport Fund is a real, working platform — not a
                  concept. Athletes create a fund page free, family/friends/club donate directly,
                  and payouts land in the athlete&apos;s own bank account.
                </span>
              </div>
            )}

            <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:items-center">
              <Link to="/signup-creator" className="group">
                <button className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 active:scale-[0.98] transition-all px-7 py-4 text-base font-semibold text-slate-900 shadow-2xl shadow-yellow-500/30">
                  Start your fund page
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                </button>
              </Link>
              <Link to="/how-it-works">
                <button className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 backdrop-blur px-6 py-4 text-base font-medium text-white transition-colors">
                  See how it works
                </button>
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-400">
              <Chip>Free forever for athletes</Chip>
              <Chip>1–3 day payouts</Chip>
              <Chip>Cancel any time</Chip>
            </div>
          </div>

          {/* Visual */}
          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-blue-500/20 via-transparent to-yellow-400/20 blur-2xl" aria-hidden />
            <div className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl shadow-black/50">
              <img
                src="/hero-athletes.jpg"
                alt="Three athletes running into a sunset"
                className="w-full h-full object-cover aspect-[4/5]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070b18]/80 via-transparent to-transparent" aria-hidden />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-yellow-300/90 font-medium">This week&apos;s training</div>
                  <div className="text-white text-lg font-semibold mt-1">Block 3 → European Cup</div>
                </div>
                <div className="rounded-xl bg-white/10 backdrop-blur border border-white/15 px-3 py-2">
                  <div className="text-[10px] uppercase text-slate-300 tracking-wider">Backers</div>
                  <div className="text-white font-bold leading-tight">37</div>
                </div>
              </div>
            </div>

            {/* Floating stat cards */}
            <div className="absolute -top-4 -left-6 sm:-left-10 hidden sm:block rounded-2xl bg-white text-slate-900 shadow-2xl shadow-black/30 px-4 py-3 ring-1 ring-slate-200/60">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center"><Wallet className="h-5 w-5" /></div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">This month</div>
                  <div className="font-bold text-base">+$1,240</div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 sm:-right-8 rounded-2xl bg-white text-slate-900 shadow-2xl shadow-black/30 px-4 py-3 ring-1 ring-slate-200/60">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-yellow-100 text-yellow-700 grid place-items-center"><Sparkles className="h-5 w-5" /></div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">Recurring</div>
                  <div className="font-bold text-base">12 backers</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent pointer-events-none" aria-hidden />
    </section>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {children}
    </span>
  );
}

/* ────────────────────────────────────────── Trust strip ── */

function TrustStrip() {
  const items = [
    { k: '$0', v: 'Platform fee for athletes' },
    { k: '100%', v: 'Of what supporters pledge' },
    { k: '1–3 days', v: 'Contribution to your bank' },
    { k: '30+', v: 'Sports supported on day one' },
  ];
  return (
    <section className="relative bg-white scroll-mt-24">
      <div className="mx-auto max-w-7xl px-6 py-20 md:py-24">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-10 md:divide-x md:divide-slate-200/80">
          {items.map((it, i) => (
            <div key={i} className={cn('text-center md:text-left', i > 0 && 'md:pl-8')}>
              <div className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900">{it.k}</div>
              <div className="mt-2 text-sm md:text-base text-slate-500 leading-tight max-w-[200px] md:max-w-none mx-auto md:mx-0">{it.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────── Money math ── */

function MoneyMath() {
  const { ref, isVisible } = useScrollAnimation(0.15);
  const cards = [
    { eq: ['12', '×', '$25/mo'], sum: '$3,600/yr', use: 'A year of coaching, or a full European club tour.', accent: 'from-blue-500/30 to-blue-700/20' },
    { eq: ['30', '×', '$10/mo'], sum: '$3,600/yr', use: 'Federation licence + a full season of entries.', accent: 'from-emerald-500/25 to-teal-700/20' },
    { eq: ['5', '×', '$100/mo'], sum: '$6,000/yr', use: 'Semi-pro budget — gear, travel, physio, the lot.', accent: 'from-yellow-400/25 to-amber-600/20' },
  ];
  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        'relative overflow-hidden bg-[#070b18] text-white transition-all duration-1000',
        isVisible ? 'translate-y-0' : 'translate-y-4'
      )}
    >
      <div aria-hidden className="absolute inset-0 bg-[radial-gradient(60%_80%_at_50%_0%,rgba(30,64,175,0.4),transparent_60%)]" />
      <div className="relative mx-auto max-w-7xl px-6 py-24 md:py-32">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-widest text-yellow-300/90 font-semibold">The math</div>
          <h2 className="mt-4 text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
            A funded season doesn&apos;t take a viral moment.
          </h2>
          <p className="mt-5 text-lg md:text-xl text-slate-300 leading-relaxed">
            It takes a small community, contributing a little every month. Sport Fund is built so even ten people in your corner go a very long way.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {cards.map((c, i) => (
            <div key={i} className="relative group rounded-3xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur-sm hover:bg-white/[0.07] transition-colors">
              <div className={cn('absolute -inset-px rounded-3xl bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none', c.accent)} aria-hidden />
              <div className="relative">
                <div className="flex items-baseline gap-2 flex-wrap text-white">
                  <span className="text-5xl md:text-6xl font-extrabold tracking-tight">{c.eq[0]}</span>
                  <span className="text-2xl text-slate-400">{c.eq[1]}</span>
                  <span className="text-3xl md:text-4xl font-bold text-slate-200">{c.eq[2]}</span>
                </div>
                <div className="mt-5 h-px bg-gradient-to-r from-yellow-400/60 to-transparent" />
                <div className="mt-5 flex items-baseline gap-2">
                  <span className="text-yellow-300 text-xs uppercase tracking-wider font-semibold">=</span>
                  <span className="text-2xl md:text-3xl font-bold text-yellow-300">{c.sum}</span>
                </div>
                <p className="mt-3 text-slate-300 text-base leading-relaxed">{c.use}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-10 text-sm text-slate-400">
          Most athletes on Sport Fund start with five to ten supporters and grow from there.
        </p>
      </div>
    </section>
  );
}

/* ───────────────────────────────────── How it works ── */

function HowItWorks() {
  const steps = [
    { n: '01', title: 'Tell your story', body: 'Pick your sport. Add a photo. Write a paragraph about what you\'re chasing this season. That\'s the page.' },
    { n: '02', title: 'Share with your people', body: 'Send the link to family, your club, your coach, your old teammates. They\'ve been waiting for an easy way to back you.' },
    { n: '03', title: 'Train. Compete. Get paid.', body: 'Stripe pays contributions straight to your bank in 1–3 business days. Sport Fund never holds your money. Post updates to keep everyone in the journey.' },
  ];
  const { ref, isVisible } = useScrollAnimation(0.1);
  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        'bg-white transition-all duration-1000',
        isVisible ? 'translate-y-0' : 'translate-y-4'
      )}
    >
      <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">How it works</div>
            <h2 className="mt-4 text-4xl md:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Live in two minutes.
            </h2>
          </div>
          <p className="md:max-w-sm text-lg text-slate-500 leading-relaxed">
            From signup to first contribution, Sport Fund is the fastest way to put your athletic career on a sustainable footing.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s) => (
            <div key={s.n} className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-50 to-white border border-slate-200/70 p-8 hover:border-slate-300 hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-xl">
              <div className="text-7xl md:text-8xl font-black tracking-tighter text-slate-100 absolute -top-2 -right-1 pointer-events-none select-none" aria-hidden>
                {s.n}
              </div>
              <div className="relative">
                <div className="text-xs font-bold tracking-widest text-blue-700">STEP {s.n}</div>
                <h3 className="mt-3 text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">{s.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────────────────── Features bento ── */

function FeaturesBento() {
  const { ref, isVisible } = useScrollAnimation(0.1);
  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        'bg-slate-50 transition-all duration-1000',
        isVisible ? 'translate-y-0' : 'translate-y-4'
      )}
    >
      <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
        <div className="max-w-3xl mb-16">
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">Built for athletes</div>
          <h2 className="mt-4 text-4xl md:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Designed around how you actually train and compete.
          </h2>
        </div>

        <div className="grid md:grid-cols-6 gap-5">
          {/* Hero card — recurring */}
          <div className="md:col-span-4 rounded-3xl bg-[#070b18] text-white p-8 md:p-10 relative overflow-hidden ring-1 ring-white/10">
            <div aria-hidden className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-yellow-400/20 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" aria-hidden />
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full bg-yellow-400/15 border border-yellow-400/30 px-3 py-1 text-xs font-semibold text-yellow-300 tracking-wide">
                <Sparkles className="h-3.5 w-3.5" /> CORE
              </div>
              <h3 className="mt-5 text-3xl md:text-4xl font-bold leading-tight tracking-tight">
                Recurring monthly support.<br />
                <span className="text-yellow-300">The way athletes actually get paid.</span>
              </h3>
              <p className="mt-4 max-w-xl text-slate-300 text-lg leading-relaxed">
                Most athletes survive on monthly support — coaching fees, gym memberships, federation costs, regular travel. Sport Fund is built to make recurring effortless, so you can plan your season instead of refreshing a campaign.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-300">
                <span className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-yellow-400" /> One-tap monthly setup</span>
                <span className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-yellow-400" /> Predictable cashflow</span>
                <span className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-yellow-400" /> Supporters can pause any time</span>
              </div>
            </div>
          </div>

          <FeatureCard icon={<Wallet className="h-6 w-6" />} title="Direct to your bank" body="Stripe sends contributions straight to your account. No middle layer, no hold." className="md:col-span-2" />
          <FeatureCard icon={<Eye className="h-6 w-6" />} title="Three visibility levels" body="Public, followers-only, supporters-only. Decide who sees what, per post." className="md:col-span-2" />
          <FeatureCard icon={<Mail className="h-6 w-6" />} title="Email built in" body="Notify your supporters with every post. Segment, schedule, track who reads." className="md:col-span-2" />
          <FeatureCard icon={<Globe className="h-6 w-6" />} title="Multi-currency" body="USD, EUR, GBP. ACH and SEPA for low-fee bank transfers." className="md:col-span-2" />

          {/* Bottom hero — athlete-first */}
          <div className="md:col-span-6 rounded-3xl bg-white border border-slate-200/70 p-8 md:p-10 relative overflow-hidden shadow-sm">
            <div className="md:flex md:items-center md:justify-between gap-10">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-800 px-3 py-1 text-xs font-semibold tracking-wide">
                  <ShieldCheck className="h-3.5 w-3.5" /> ATHLETE-FIRST
                </div>
                <h3 className="mt-5 text-3xl md:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
                  No 30% skim. <span className="text-blue-700">No tipping defaults.</span> No upsells.
                </h3>
                <p className="mt-4 text-slate-600 text-lg leading-relaxed">
                  Athletes pay $0 to use Sport Fund. A small, transparent fee is added to each contribution to cover Stripe and the platform — and you receive 100% of every dollar your supporters pledge. We never touch your money.
                </p>
              </div>
              <div className="hidden md:flex flex-col gap-3 mt-8 md:mt-0 shrink-0">
                <div className="rounded-2xl bg-slate-50 border border-slate-200/70 px-5 py-4 min-w-[200px]">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Athletes pay</div>
                  <div className="text-3xl font-extrabold text-slate-900 mt-1">$0</div>
                </div>
                <div className="rounded-2xl bg-slate-50 border border-slate-200/70 px-5 py-4 min-w-[200px]">
                  <div className="text-xs uppercase tracking-wider text-slate-500">You receive</div>
                  <div className="text-3xl font-extrabold text-emerald-700 mt-1">100%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ icon, title, body, className }: { icon: React.ReactNode; title: string; body: string; className?: string }) {
  return (
    <div className={cn('rounded-3xl bg-white border border-slate-200/70 p-7 hover:border-slate-300 hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-lg', className)}>
      <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-700 grid place-items-center">{icon}</div>
      <h3 className="mt-5 text-xl font-bold tracking-tight text-slate-900">{title}</h3>
      <p className="mt-2 text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

/* ──────────────────────────────────── Demo showcase ── */

function DemoShowcase() {
  return (
    <section className="relative overflow-hidden bg-[#070b18] text-white">
      <div aria-hidden className="absolute inset-0 bg-[radial-gradient(80%_60%_at_0%_50%,rgba(30,64,175,0.35),transparent_60%),radial-gradient(60%_50%_at_100%_50%,rgba(250,204,21,0.12),transparent_60%)]" />
      <div className="relative mx-auto max-w-7xl px-6 py-24 md:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-yellow-300/90 font-semibold">See it live</div>
            <h2 className="mt-4 text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              This is what your fund page looks like.
            </h2>
            <p className="mt-5 text-lg text-slate-300 max-w-xl leading-relaxed">
              Take a tour of a complete fund page — story, season goals, club, achievements, recurring & one-off support. Built so supporters can back you in under thirty seconds.
            </p>

            <ul className="mt-8 space-y-3 text-slate-200">
              {[
                'Your story + intro video',
                'Sport, level, club, achievements, sponsors',
                'Season goals + training updates',
                'One-off and monthly support, multiple currencies',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-yellow-400 mt-0.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/fund/demo">
                <button className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-white hover:bg-slate-100 text-slate-900 px-7 py-4 text-base font-semibold transition-colors shadow-xl shadow-black/20">
                  Tour a demo fund page
                  <ArrowUpRight className="h-5 w-5" />
                </button>
              </Link>
              <Link to="/signup-creator">
                <button className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 backdrop-blur px-6 py-4 text-base font-medium text-white transition-colors">
                  Build mine
                </button>
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-yellow-400/20 via-transparent to-blue-500/20 blur-3xl" aria-hidden />
            <div className="relative rounded-3xl overflow-hidden bg-white text-slate-900 ring-1 ring-white/10 shadow-2xl shadow-black/40">
              <div className="relative h-44 bg-gradient-to-br from-blue-700 to-indigo-900">
                <img src="/hero-training.jpg" alt="Cover" className="absolute inset-0 h-full w-full object-cover opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" aria-hidden />
              </div>
              <div className="relative -mt-12 px-6 pb-6">
                <div className="flex items-end gap-4">
                  <img src="/athlete-smiling.jpg" alt="Maria Santos" className="h-24 w-24 rounded-2xl ring-4 ring-white object-cover" />
                  <div className="pb-1">
                    <div className="text-xl font-bold text-slate-900">Maria Santos</div>
                    <div className="text-sm text-slate-500">Athletics — 800m / 1500m</div>
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  {['Semi-Professional', 'U23', 'Bay Area Track Club'].map((c) => (
                    <span key={c} className="rounded-full bg-blue-50 text-blue-800 text-xs font-medium px-3 py-1 border border-blue-100">{c}</span>
                  ))}
                </div>
                <div className="mt-5 rounded-2xl bg-amber-50 border border-amber-200 p-4">
                  <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Next goal</div>
                  <div className="mt-1 text-slate-900 font-semibold">US Olympic Trials 2028</div>
                </div>
                <div className="mt-5 grid grid-cols-3 gap-3">
                  <div className="rounded-xl bg-slate-50 border border-slate-200/70 p-3 text-center">
                    <div className="text-lg font-bold text-slate-900">$1.2k</div>
                    <div className="text-[10px] uppercase text-slate-500 mt-0.5">This month</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-200/70 p-3 text-center">
                    <div className="text-lg font-bold text-slate-900">12</div>
                    <div className="text-[10px] uppercase text-slate-500 mt-0.5">Monthly backers</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-200/70 p-3 text-center">
                    <div className="text-lg font-bold text-emerald-700">+25%</div>
                    <div className="text-[10px] uppercase text-slate-500 mt-0.5">Vs last mo.</div>
                  </div>
                </div>
                <button className="mt-5 w-full rounded-full bg-blue-900 hover:bg-blue-800 text-white py-3 font-semibold text-sm flex items-center justify-center gap-2">
                  <Trophy className="h-4 w-4 text-yellow-300" /> Support Maria&apos;s journey
                </button>
              </div>
              <div className="absolute top-3 right-3">
                <span className="rounded-full bg-yellow-400 text-slate-900 text-[10px] font-bold tracking-wider px-2.5 py-1 uppercase shadow">Demo</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────── FAQ teaser ── */

const FAQS = [
  { q: 'Is it really free for athletes?', a: 'Yes — athletes pay nothing, ever. A small, transparent fee is added to each contribution to cover Stripe and the platform. You receive 100% of what your supporters pledge.' },
  { q: 'How fast do I get paid?', a: 'Stripe processes contributions immediately and deposits them into your bank account on the standard payout schedule — typically 1–3 business days. Sport Fund never holds your money.' },
  { q: 'Can supporters cancel any time?', a: 'Yes. Recurring contributions can be cancelled from the supporter\'s dashboard or by emailing support, no commitments. We make the cancel button easy to find on purpose.' },
  { q: 'What sports are supported?', a: 'Thirty-plus sports on day one — athletics, cycling, swimming, football, rugby, combat sports, esports, motorsport, equestrian, water sports and more. If your sport isn\'t listed, write to us and we\'ll add it.' },
];

function FaqTeaser() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-4xl px-6 py-24 md:py-32">
        <div className="text-center mb-14">
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">Things you might be wondering</div>
          <h2 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            The questions athletes ask first.
          </h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                className="w-full text-left rounded-2xl border border-slate-200/70 bg-white px-6 py-5 hover:border-slate-300 transition-colors"
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="font-semibold text-lg text-slate-900">{f.q}</span>
                  <ChevronDown className={cn('h-5 w-5 text-slate-400 transition-transform shrink-0', isOpen && 'rotate-180')} />
                </div>
                <div className={cn('grid transition-all duration-300', isOpen ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0')}>
                  <div className="overflow-hidden">
                    <p className="text-slate-600 leading-relaxed">{f.a}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-8 text-center">
          <Link to="/faqs" className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-800 font-semibold">
            All FAQs <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────── Final CTA ── */

function FinalCTA() {
  return (
    <section className="relative overflow-hidden bg-[#070b18] text-white">
      <div aria-hidden className="absolute inset-0 bg-[radial-gradient(100%_80%_at_50%_0%,rgba(30,64,175,0.45),transparent_60%),radial-gradient(60%_60%_at_100%_100%,rgba(250,204,21,0.18),transparent_60%)]" />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-[480px] w-[480px] rounded-full bg-yellow-400/10 blur-3xl" aria-hidden />
      <div className="relative mx-auto max-w-5xl px-6 py-28 md:py-40 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 text-xs font-medium tracking-wider uppercase text-yellow-300/90">
          <Zap className="h-3.5 w-3.5" /> Two minutes to launch
        </div>
        <h2 className="mt-7 text-5xl md:text-7xl xl:text-8xl font-extrabold tracking-tight leading-[0.95] text-balance">
          Your people are <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent">already</span> in your corner.
          <br />
          <span className="text-slate-300">Give them a way to actually back you.</span>
        </h2>
        <p className="mt-8 max-w-2xl mx-auto text-lg md:text-xl text-slate-300 leading-relaxed">
          Free forever for athletes. No card needed to start. Get your fund page live in under two minutes.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/signup-creator">
            <button className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-slate-900 px-9 py-5 text-lg font-semibold shadow-2xl shadow-yellow-500/30 transition-all active:scale-[0.98]">
              Start your fund page
              <ArrowRight className="h-5 w-5" />
            </button>
          </Link>
          <Link to="/contact">
            <button className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 backdrop-blur px-7 py-5 text-lg font-medium text-white transition-colors">
              Talk to us
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
}
